import type { MatchResultDto, RoomDto } from './api';
import type { FinishStatus, RematchKind } from './domain';

/**
 * Realtime contract.
 *
 * Only the server publishes broadcast events. Clients never broadcast game data: in
 * production the realtime.messages RLS policies allow clients to *receive* broadcasts and
 * to *track presence* on topics they are members of, nothing else. Scores shown from these
 * events are informational; the official result always comes from GET /matches/{id}/result.
 */
export const roomTopic = (roomId: string) => `game:room:${roomId}`;
export const matchTopic = (matchId: string) => `game:match:${matchId}`;

export type RealtimeEvent =
  | { type: 'PLAYER_JOINED'; roomId: string; userId: string; room: RoomDto }
  | { type: 'PLAYER_LEFT'; roomId: string; userId: string; room: RoomDto }
  | { type: 'PLAYER_READY'; roomId: string; userId: string; isReady: boolean; room: RoomDto }
  | { type: 'ROOM_UPDATED'; roomId: string; room: RoomDto }
  | { type: 'MATCH_STARTING'; roomId: string; matchId: string; startsAt: number; endsAt: number; serverTime: number }
  | { type: 'MATCH_STARTED'; matchId: string; serverTime: number }
  | {
      type: 'SCORE_UPDATED';
      matchId: string;
      userId: string;
      score: number;
      moveCount: number;
      finishStatus: FinishStatus;
      serverTime: number;
    }
  | { type: 'PLAYER_FORFEITED'; matchId: string; userId: string; serverTime: number }
  | { type: 'MATCH_FINISHED'; matchId: string; roomId: string; result: MatchResultDto }
  | { type: 'ROOM_CLOSED'; roomId: string; status: 'CANCELLED' | 'EXPIRED' }
  | { type: 'ROOM_REMATCH'; roomId: string; kind: RematchKind; targetRoomId: string; userIds: string[] };

export type RealtimeEventType = RealtimeEvent['type'];

/** Presence payload tracked by each client on room and match topics. */
export interface PresenceState {
  userId: string;
  nickname: string;
  sessionId: string;
  onlineAt: number;
}

/* ---------------- local WebSocket hub protocol (development / Node hosting) */

export type WsClientMessage =
  /** Must be the first message; the token never goes into the WebSocket URL. */
  | { op: 'auth'; token: string }
  | { op: 'subscribe'; topic: string; ref: string }
  | { op: 'unsubscribe'; topic: string }
  | { op: 'presence'; topic: string; state: PresenceState }
  | { op: 'ping' };

export type WsServerMessage =
  | { op: 'authed'; userId: string }
  | { op: 'subscribed'; topic: string; ref: string }
  | { op: 'error'; ref?: string; topic?: string; message: string }
  | { op: 'event'; topic: string; event: RealtimeEvent }
  | { op: 'presence_sync'; topic: string; presences: PresenceState[] }
  | { op: 'pong'; serverTime: number };
